/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ModelResponse, navigate, ResponseData} from '@dvrd/dvr-controls';

import EmailTemplate from '../../../models/emailTemplateModel';

import EmailStep from '../../../models/emailStepModel';

import {AppContext} from '../../misc/appContext';

import {createErrorMessage} from '../../../util/utils';

import {onShowConfirmSnackbar} from '../../../util/renderUtil';

import EmailTemplatePreview from './preview/emailTemplatePreview';

import {ActivateHandle, EmailFlow, UnlayerDesign} from '../../../util/interfaces';

import {TemplateParams} from '../../../util/emailStepUtil';

import TemplateSelect from './templateSelect';

import {setNavigationParam} from '../../../util/navigateUtil';

import {EMAIL_TEMPLATE_ROUTE} from '../../../util/constants';

import DefaultEmail from '../../../models/defaultEmailModel';

import {showDialog} from '@dvrd/dvr-controls';

export enum TemplateTab {
    DEFAULT = 'DEFAULT',
    CUSTOM = 'CUSTOM'
}

interface Props {
    onReplaceStep: (step: EmailStep) => void;
    onNavigateTemplate?: (template: EmailTemplate, step: EmailStep, returnPage?: string) => void;
    returnPage?: string;
    flows?: Array<EmailFlow>;
}

function TemplateSelectController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {returnPage, onReplaceStep, onNavigateTemplate, flows} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [defaultEmails, setDefaultEmails] = useState<Array<DefaultEmail>>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [steps, setSteps] = useState<Array<EmailStep>>([]);
    const [activeTab, setActiveTab] = useState<TemplateTab>(TemplateTab.DEFAULT);
    const [step, setStep] = useState<EmailStep>(new EmailStep());
    const [showAll, setShowAll] = useState(false);
    const previewRef = useRef<ActivateHandle>(null);

    function onActivate(step: EmailStep, openCustom: boolean = false) {
        const tab = openCustom ? TemplateTab.CUSTOM : TemplateTab.DEFAULT;
        setActiveTab(tab);
        setStep(step);
        setActive(true);
    }

    function onToggleShowAll() {
        setShowAll(!showAll);
    }

    function onChangeTab(tab: TemplateTab) {
        setActiveTab(tab);
        setShowAll(false);
    }

    function onChooseTemplate(template: EmailTemplate) {
        return function () {
            const usedElsewhere = steps.some((stp: EmailStep) => stp.id !== step.id && stp.templateId === template.id);
            if (usedElsewhere)
                showDialog(
                    t(
                        'email.templateSelect.inUseWarning',
                        'Deze template wordt ook al gebruikt in andere e-mailflows. Wijzigingen die je hier aanbrengt, worden ook doorgevoerd in die flows. Wil je dit niet? Maak dan een kopie van het template.'
                    ),
                    t('email.templateSelect.inUseTitle', 'Template al in gebruik'),
                    [
                        t('email.templateSelect.cancel', 'Annuleren'),
                        {
                            label: t('email.templateSelect.chooseAnyway', 'Toch kiezen'),
                            onClick: onConfirmChooseTemplate(template),
                            primary: false
                        }
                    ]
                );
            else onConfirmChooseTemplate(template)();
        };
    }

    function onConfirmChooseTemplate(template: EmailTemplate) {
        return function () {
            const stp = step.clone();
            stp.templateId = template.id;
            setLoading(true);
            updateStep(stp, (step: EmailStep, success: boolean, data: ResponseData) => {
                if (success) {
                    onShowConfirmSnackbar(t('email.templateSelect.emailUpdated', 'De e-mail is bijgewerkt'));
                    onClose();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('email.templateSelect.updateFailed', 'Het bijwerken van de e-mail is niet gelukt.')
                        ),
                        t('email.templateSelect.updateFailedTitle', 'Bijwerken mislukt')
                    );
            });
        };
    }

    function onOpenTemplate(template: EmailTemplate) {
        return function () {
            if (onNavigateTemplate) return onNavigateTemplate(template, step, returnPage);
            else {
                setNavigationParam({template, step, returnPage});
                navigate(EMAIL_TEMPLATE_ROUTE);
            }
        };
    }

    function onChooseDefault(defaultEmail?: DefaultEmail) {
        return function () {
            const {account} = context.accountContext;
            if (!account) return;
            setLoading(true);
            let params: TemplateParams;
            if (defaultEmail)
                params = {
                    name: defaultEmail.templateName,
                    subject: defaultEmail.emailSubject,
                    json: defaultEmail.jsonTemplate
                };
            else
                params = {
                    name: t('email.templateSelect.newTemplateName', 'Nieuwe E-mail Template'),
                    subject: t(
                        'email.templateSelect.newTemplateSubject',
                        'Over jouw bestelling {order_id} van {bedrijf_naam} via Bol'
                    ),
                    json: {} as UnlayerDesign
                };
            const {name, subject, json} = params;
            const {email, companyName, id} = account;
            const withTestInvoice =
                [EmailFlow.INVOICE, EmailFlow.REVIEW_INVOICE].includes(step.flow) || step.withInvoice;
            const template = new EmailTemplate({
                account_id: id,
                name,
                subject,
                with_test_invoice: withTestInvoice,
                json,
                sender: companyName,
                reply_to: email
            });
            onOpenTemplate(template)();
        };
    }

    function onPreviewTemplate(
        body: string,
        isHTML: boolean = false,
        isLegacy: boolean = false,
        bcc: string | null = null,
        subject: string | null = null
    ) {
        previewRef.current?.activate(body, isHTML, isLegacy, bcc, subject);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setTemplates([]);
        setSteps([]);
        setActiveTab(TemplateTab.DEFAULT);
        setShowAll(false);
    }

    function onDeleteTemplate(template: EmailTemplate) {
        return function () {
            showDialog(
                t('email.templateSelect.deleteConfirm', 'Weet je zeker dat je deze template wil verwijderen?'),
                t('email.templateSelect.deleteTitle', 'Template verwijderen'),
                [
                    t('email.templateSelect.no', 'Nee'),
                    {
                        label: t('email.templateSelect.yes', 'Ja'),
                        onClick: onConfirmDeleteTemplate(template),
                        primary: false
                    }
                ]
            );
        };
    }

    function onConfirmDeleteTemplate(template: EmailTemplate) {
        return function () {
            setLoading(true);
            template.delete((template: EmailTemplate, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setTemplates(templates.filter((temp: EmailTemplate) => temp.id !== template.id));
                    onShowConfirmSnackbar(t('email.templateSelect.deleted', 'De E-mail Template is verwijderd'));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'email.templateSelect.deleteFailed',
                                    'Het verwijderen van de E-mail Template is niet gelukt.'
                                )
                        ),
                        t('email.templateSelect.deleteFailedTitle', 'Verwijderen mislukt')
                    );
            });
        };
    }

    function onDuplicateTemplate(template: EmailTemplate) {
        return function () {
            setLoading(true);
            template.duplicate((template: EmailTemplate | null, success: boolean, data: ResponseData) => {
                setLoading(true);
                if (success) {
                    onShowConfirmSnackbar(t('email.templateSelect.copied', 'De template is gekopieerd'));
                    onOpenTemplate(template!)();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('email.templateSelect.copyFailed', 'Het kopiëren van de template is niet gelukt.')
                        ),
                        t('email.templateSelect.copyFailedTitle', 'Kopiëren mislukt')
                    );
            });
        };
    }

    function updateStep(step: EmailStep, callback: ModelResponse<EmailStep>) {
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            if (success) onReplaceStep(step);
            if (callback) callback(step, success, data);
        });
    }

    function loadAll() {
        setLoading(true);
        Promise.all([loadTemplates(), loadDefaultEmails(), loadSteps()]).finally(() => {
            setLoading(false);
        });
    }

    function loadSteps() {
        return new Promise<void>(resolve => {
            const {account} = context.accountContext;
            if (!account) return resolve();
            EmailStep.getAll(account.id, null, (steps: EmailStep[], success: boolean) => {
                if (success) setSteps(steps);
                resolve();
            });
        });
    }

    function loadTemplates() {
        return new Promise<void>((resolve, reject) => {
            const {account} = context.accountContext;
            if (!account) return reject();
            setLoading(true);
            EmailTemplate.getAll(
                account.id,
                -1,
                -1,
                (templates: EmailTemplate[], success: boolean, data: ResponseData) => {
                    if (success) setTemplates(templates);
                    else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'email.templateSelect.loadTemplatesFailed',
                                        'Het laden van de e-mail templates is niet gelukt.'
                                    )
                            ),
                            t('email.templateSelect.loadFailedTitle', 'Laden mislukt')
                        );
                    resolve();
                }
            );
        });
    }

    function loadDefaultEmails() {
        const _flows = flows ?? [EmailFlow.INVOICE, EmailFlow.REVIEW_INVOICE];
        return new Promise<void>(resolve => {
            DefaultEmail.getAll({
                flows: _flows,
                callback: (defaultEmails: Array<DefaultEmail>, success: boolean, data: ResponseData) => {
                    if (success) setDefaultEmails(defaultEmails);
                    else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'email.templateSelect.loadDefaultsFailed',
                                        'Het laden van de standaard templates is niet gelukt.'
                                    )
                            ),
                            t('email.templateSelect.loadFailedTitle', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    useEffect(() => {
        if (active) loadAll();
    }, [active]);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <>
            <TemplateSelect
                onClose={onClose}
                onChooseDefault={onChooseDefault}
                onChooseTemplate={onChooseTemplate}
                onChangeTab={onChangeTab}
                onOpenTemplate={onOpenTemplate}
                onPreviewTemplate={onPreviewTemplate}
                onDeleteTemplate={onDeleteTemplate}
                onToggleShowAll={onToggleShowAll}
                onDuplicateTemplate={onDuplicateTemplate}
                templates={templates}
                loading={loading}
                active={active}
                activeTab={activeTab}
                currentTemplateId={step.templateId}
                flow={step.flow}
                showAll={showAll}
                defaultEmails={defaultEmails}
            />
            <EmailTemplatePreview ref={previewRef} />
        </>
    );
}

export default forwardRef<ActivateHandle, Props>(TemplateSelectController);
